import { useAuth } from "../context/useAuth";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Space,
  Modal,
  message,
  Input,
  Spin,
  Drawer,
  Col,
} from "antd";
import CreateModal from "../component/adminComponent/createModal";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import UpdateProductGroupModal from "../component/adminComponent/updateProductGroup";
import AdminLayout from "../component/adminComponent/adminLayout";
import CloneModal from "../component/adminComponent/cloneModal";
import axios from "axios";
import CreateOrderModal from "../component/adminComponent/createOrder";
import AdminLoading from "../component/adminComponent/adminLoading";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import CreateProductAdminPage from "../component/adminComponent/createProductAdminPage";
import {
  getCachedApps,
  resetAppsInFlightRequest,
} from "../utils/appCache";
import {
  loadAdminProductGroups,
  readAdminProductGroupsCache,
  writeAdminProductGroupsCache,
} from "../services/adminProductGroups";
import {
  readAdminInventoryCache,
  writeAdminInventoryCache,
} from "../services/adminInventoryCache";

const getTimestampMillis = (value) => {
  if (typeof value?.toMillis === "function") return value.toMillis();
  const seconds = Number(value?.seconds ?? value?._seconds);
  return Number.isFinite(seconds) ? seconds * 1000 : 0;
};

const sortProductGroups = (items) =>
  [...items].sort((a, b) => {
    const aOrder = Number(a.sortOrder);
    const bOrder = Number(b.sortOrder);
    const hasAOrder = Number.isFinite(aOrder);
    const hasBOrder = Number.isFinite(bOrder);

    if (hasAOrder && hasBOrder) return aOrder - bOrder;
    if (hasAOrder) return -1;
    if (hasBOrder) return 1;

    return getTimestampMillis(b.createdAt) - getTimestampMillis(a.createdAt);
  });

function InventoryLoadingState({ loading, error, onRetry }) {
  if (loading) return <Spin size="small" />;
  if (error) {
    return <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ color: "#fbbf24" }}>โหลด stock ไม่สำเร็จ</span><Button size="small" onClick={onRetry}>ลองใหม่</Button></div>;
  }
  return null;
}

const appendImageRetryToken = (url, token) => {
  if (!url || !token || String(url).startsWith("data:")) return url;
  const separator = String(url).includes("?") ? "&" : "?";
  return `${url}${separator}cinema_retry=${encodeURIComponent(token)}`;
};

function AdminImage({
  src,
  retryVersion = 0,
  fallback = "/noImage.jpg",
  alt = "",
  ...props
}) {
  const [attempt, setAttempt] = useState(0);
  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setUseFallback(false);
  }, [src, retryVersion]);

  const imageSrc = useFallback || !src
    ? fallback
    : appendImageRetryToken(src, `${retryVersion}-${attempt}`);

  return (
    <img
      {...props}
      src={imageSrc}
      alt={alt}
      onError={() => {
        if (!src || useFallback) return;
        if (attempt < 1) {
          setAttempt((current) => current + 1);
          return;
        }
        setUseFallback(true);
      }}
    />
  );
}

export default function Admin() {
  const initialProductGroupsRef = useRef(readAdminProductGroupsCache());
  const initialInventoryRef = useRef(readAdminInventoryCache());
  const [isMobile, setIsMobile] = useState((window.innerWidth <= 1180 || window.matchMedia("(max-width: 1366px) and (pointer: coarse)").matches));
  const { user, loading, error: authError } = useAuth();
  const [data, setData] = useState(() =>
    sortProductGroups(initialProductGroupsRef.current),
  );
  const [inventory, setInventory] = useState(initialInventoryRef.current);
  const [loadingInv, setLoadingInv] = useState(
    () => initialInventoryRef.current.length === 0,
  );
  const [inventoryError, setInventoryError] = useState(false);
  const [loadingData, setLoadingData] = useState(
    () => initialProductGroupsRef.current.length === 0,
  );
  const [dataLoadError, setDataLoadError] = useState(null);
  const [resumeVersion, setResumeVersion] = useState(0);
  const [imageRetryVersion, setImageRetryVersion] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [selectProduct, setSelectProduct] = useState();
  const [openDrawer, setOpenDrawer] = useState(false);

  const [appList, setAppList] = useState();
  const productRequestRef = useRef(0);
  const inventoryControllerRef = useRef(null);
  const appsRequestRef = useRef(0);

  const filteredData = useMemo(() => {
    if (!keyword) return data;
    const lowerKeyword = keyword.toLowerCase();
    return data.filter(
      (item) =>
        item.name?.toLowerCase().includes(lowerKeyword) ||
        item.app?.toLowerCase().includes(lowerKeyword),
    );
  }, [data, keyword]);
  const pagedData = filteredData;
  const inventoryByGroupId = useMemo(() => {
    const groupedInventory = new Map();

    inventory.forEach((item) => {
      const current = groupedInventory.get(item.groupId) || [];
      current.push(item);
      groupedInventory.set(item.groupId, current);
    });

    return new Map(
      [...groupedInventory.entries()].map(([groupId, items]) => {
        const standaloneItems = [];
        const itemsByStockPool = new Map();

        items.forEach((item) => {
          if (!item.stockPool) {
            standaloneItems.push(item);
            return;
          }

          const poolItems = itemsByStockPool.get(item.stockPool) || [];
          poolItems.push(item);
          itemsByStockPool.set(item.stockPool, poolItems);
        });

        const sharedInventory = [...itemsByStockPool.values()].flatMap(
          (poolItems) => {
            const masterItem = poolItems.find((item) => item.stockPoolMaster);

            // ถ้ายังไม่มี Master ให้แสดงตัวเลือกแยกกันเหมือนคลังปกติ
            if (!masterItem) return poolItems;

            const orderedItems = [...poolItems].sort(
              (a, b) => Number(Boolean(b.stockPoolMaster)) - Number(Boolean(a.stockPoolMaster)),
            );

            return [
              {
                ...masterItem,
                optionName: orderedItems
                  .map((item) => item.optionName)
                  .filter(Boolean)
                  .join(" / "),
              },
            ];
          },
        );

        return [groupId, [...standaloneItems, ...sharedInventory]];
      }),
    );
  }, [inventory]);

  const getInventoryState = (inventoryItem) => {
    const stock = Number(inventoryItem?.stock || 0);
    const alertInventory = Number(inventoryItem?.alertInventory || 0);

    if (stock <= 0) return "danger";

    return alertInventory > 0 && stock <= alertInventory ? "warning" : "success";
  };
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile((window.innerWidth <= 1180 || window.matchMedia("(max-width: 1366px) and (pointer: coarse)").matches));
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!loadingData) return undefined;

    // This guard is intentionally independent from requestId. PWA resume can
    // replace an in-flight request; the UI must still never spin forever.
    const hardLoadingTimeoutId = window.setTimeout(() => {
      const emergencyCache = readAdminProductGroupsCache();

      if (emergencyCache.length > 0) {
        setData(sortProductGroups(emergencyCache));
      }

      setLoadingData(false);
      setDataLoadError((currentError) =>
        currentError ||
        (emergencyCache.length > 0
          ? "กำลังแสดงข้อมูลล่าสุดที่บันทึกไว้ เนื่องจากเชื่อมต่อช้า"
          : "โหลดรายการสินค้าใช้เวลานานเกินไป กรุณากดลองใหม่"),
      );
    }, 10000);

    return () => window.clearTimeout(hardLoadingTimeoutId);
  }, [loadingData]);

  const fetchData = async () => {
    const requestId = ++productRequestRef.current;
    const needsBlockingLoading = data.length === 0;
    if (needsBlockingLoading) setLoadingData(true);

    // A suspended Safari request can occasionally remain pending without
    // reaching axios/Firestore's own timeout callbacks. Never leave the page
    // in a permanent loading state; a late successful response may still
    // replace this error and render normally.
    const loadingWatchdogId = needsBlockingLoading
      ? window.setTimeout(() => {
          if (requestId !== productRequestRef.current) return;
          setLoadingData(false);
          setDataLoadError(
            "โหลดรายการสินค้าใช้เวลานานเกินไป กรุณากดลองใหม่",
          );
        }, 12000)
      : null;

    try {
      let list;
      try {
        list = await loadAdminProductGroups(user);
      } catch (apiError) {
        console.warn("Admin product API failed, using Firestore fallback:", apiError);
        let fallbackTimeoutId;
        const fallbackTimeout = new Promise((_, reject) => {
          fallbackTimeoutId = window.setTimeout(
            () => reject(new Error("โหลดรายการสินค้าใช้เวลานานเกินไป")),
            7000,
          );
        });

        try {
          const querySnapshot = await Promise.race([
            getDocs(
              query(
                collection(db, "productGroups"),
                where("status", "==", "available"),
                where("cloneTo", "==", null),
              ),
            ),
            fallbackTimeout,
          ]);
          list = querySnapshot.docs.map((productGroupDoc) => ({
            key: productGroupDoc.id,
            ...productGroupDoc.data(),
          }));
          writeAdminProductGroupsCache(list);
        } finally {
          window.clearTimeout(fallbackTimeoutId);
        }
      }

      if (requestId !== productRequestRef.current) return;
      setDataLoadError(null);
      setData(sortProductGroups(list));
      setOpenDrawer(false);
    } catch (error) {
      if (requestId !== productRequestRef.current) return;
      console.error("Error fetching data: ", error);
      setDataLoadError(error?.message || "ไม่สามารถโหลดรายการสินค้าได้");
    } finally {
      if (loadingWatchdogId) window.clearTimeout(loadingWatchdogId);
      if (requestId === productRequestRef.current) {
        setLoadingData(false);
      }
    }
  };

  const moveProduct = async (record, direction) => {
    const currentIndex = data.findIndex((item) => item.key === record.key);
    const targetIndex = currentIndex + direction;

    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= data.length) return;

    const targetProduct = data[targetIndex];
    const currentOrder = Number.isFinite(Number(record.sortOrder))
      ? Number(record.sortOrder)
      : currentIndex;
    const targetOrder = Number.isFinite(Number(targetProduct.sortOrder))
      ? Number(targetProduct.sortOrder)
      : targetIndex;

    try {
      await Promise.all([
        updateDoc(doc(db, "productGroups", record.key), {
          sortOrder: targetOrder,
          updatedAt: serverTimestamp(),
        }),
        updateDoc(doc(db, "productGroups", targetProduct.key), {
          sortOrder: currentOrder,
          updatedAt: serverTimestamp(),
        }),
      ]);
      await fetchData();
    } catch (error) {
      console.error("Move product error:", error);
      message.error("ไม่สามารถเปลี่ยนลำดับสินค้าได้");
    }
  };

  const loadInventory = async () => {
    inventoryControllerRef.current?.abort();
    const controller = new AbortController();
    inventoryControllerRef.current = controller;
    const startedAt = performance.now();
    const hasCachedInventory = inventory.length > 0;
    setLoadingInv(!hasCachedInventory);
    setInventoryError(false);

    try {
      const response = await axios.post(
        "https://asia-southeast1-mailcinemaotp.cloudfunctions.net/getInventorySummary",
        {},
        { timeout: 15000, signal: controller.signal },
      );

      if (controller.signal.aborted) return;

      if (!response.data.success) {
        throw new Error(response.data.message || "โหลด stock ไม่สำเร็จ");
      }

      const durationMs = Math.round(performance.now() - startedAt);
      const nextInventory = response.data.data || [];
      setInventory(nextInventory);
      writeAdminInventoryCache(nextInventory);
      console.info("[performance] admin.loadInventory", {
        durationMs,
        itemCount: Array.isArray(nextInventory) ? nextInventory.length : 0,
      });
      if (durationMs >= 10000) {
        console.warn("[performance] admin.loadInventory.slow", { durationMs });
      }
    } catch (error) {
      if (controller.signal.aborted || error?.code === "ERR_CANCELED") return;
      console.error("Load inventory failed:", error);
      console.warn("[performance] admin.loadInventory.failed", {
        durationMs: Math.round(performance.now() - startedAt),
      });
      // ถ้ามี cache ให้คงข้อมูลเดิมไว้และไม่เปลี่ยนทุกแถวเป็น spinner/error
      setInventoryError(!hasCachedInventory);
    } finally {
      if (!controller.signal.aborted) setLoadingInv(false);
    }
  };
  

  const getClaimOrderMessage = async () => {
    const requestId = ++appsRequestRef.current;
    try {
      const apps = (await getCachedApps()).filter(Boolean);

      if (requestId !== appsRequestRef.current) return null;
      setAppList(apps);
      return "complete";
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  useEffect(() => {
    if (!user) return undefined;

    fetchData();
    loadInventory();
    getClaimOrderMessage();
    return () => {
      productRequestRef.current += 1;
      appsRequestRef.current += 1;
      inventoryControllerRef.current?.abort();
    };
    // fetchData/loadInventory/getClaimOrderMessage intentionally rerun only
    // when the authenticated user changes, not after each state update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, resumeVersion]);

  useEffect(() => {
    const handleResume = (event) => {
      resetAppsInFlightRequest();
      setImageRetryVersion(
        Number(event?.detail?.resumedAt) || Date.now(),
      );
      setResumeVersion((current) => current + 1);
    };

    window.addEventListener("cinema:resume", handleResume);
    return () => window.removeEventListener("cinema:resume", handleResume);
  }, []);

  if (loading) return <AdminLoading />;
  if (authError) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#111", color: "#f87171", padding: 24, textAlign: "center" }}>
        <div>
          <div>ตรวจสอบการเข้าสู่ระบบไม่สำเร็จ</div>
          <Button style={{ marginTop: 12 }} onClick={() => window.location.reload()}>
            ลองใหม่
          </Button>
        </div>
      </div>
    );
  }
  if (!user) return null;

  const handleDelete = (group) => {
    setOpenDrawer(false);
    Modal.confirm({
      className: "neo-modal",
      title: <div style={{ color: "white" }}>ลบตัวเลือก?</div>,
      content: (
        <div>
          <div style={{ color: "white" }}>
            คุณต้องการลบตัวเลือกนี้ใช่หรือไม่?
          </div>

          <div
            style={{
              marginTop: 8,
              padding: 12,
              borderRadius: 12,
              background: "rgba(239,68,68,.1)",
              color: "#f87171",
            }}
          >
            {group?.name || "-"}
          </div>
        </div>
      ),

      okText: "ลบ",
      cancelText: "ยกเลิก",

      okButtonProps: {
        className: "neo-delete-btn",
      },

      cancelButtonProps: {
        className: "neo-cancel-btn",
      },

      async onOk() {
        try {
          const groupId = group.key;
          const optionsRef = collection(
            db,
            "productGroups",
            groupId,
            "options",
          );

          const optionsSnapshot = await getDocs(optionsRef);

          const options = optionsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          let hasSoldProduct = false;

          for (const option of options) {
            const productsRef = collection(
              db,
              "productGroups",
              groupId,
              "options",
              option.id,
              "products",
            );

            const productsSnapshot = await getDocs(productsRef);

            const products = productsSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            const foundSold = products.some(
              (product) =>
                Number(product.amount) > Number(product.remainAmount),
            );

            if (foundSold) {
              hasSoldProduct = true;
              break;
            }
          }

          const groupRef = doc(db, "productGroups", groupId);

          // เคยขายแล้ว => Soft Delete
          if (hasSoldProduct) {
            await updateDoc(groupRef, {
              status: "deleted",
              deletedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            });
            setOpenDrawer(false);
            setSelectProduct(undefined);
            await Promise.all([fetchData(), loadInventory()]);
            return;
          }

          // ไม่เคยขาย => ลบ products -> options -> group

          for (const option of options) {
            const productsRef = collection(
              db,
              "productGroups",
              groupId,
              "options",
              option.id,
              "products",
            );

            const productsSnapshot = await getDocs(productsRef);

            await Promise.all(
              productsSnapshot.docs.map((productDoc) =>
                deleteDoc(productDoc.ref),
              ),
            );

            await deleteDoc(
              doc(db, "productGroups", groupId, "options", option.id),
            );
          }

          await deleteDoc(groupRef);
          setOpenDrawer(false);
          setSelectProduct(undefined);
          await Promise.all([fetchData(), loadInventory()]);
        } catch (error) {
          console.error(error);
          message.error("ลบสินค้าไม่สำเร็จ");
        }
      },
    });
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "sortOrder",
      width: 92,
      align: "center",
      render: (_, record) => {
        const index = data.findIndex((item) => item.key === record.key);

        return (
          <Space size={4}>
            {index > 0 && (
              <Button
                className="neo-btn-show"
                size="small"
                icon={<ArrowUpOutlined />}
                onClick={() => moveProduct(record, -1)}
              />
            )}
            {index < data.length - 1 && (
              <Button
                className="neo-btn-show"
                size="small"
                icon={<ArrowDownOutlined />}
                onClick={() => moveProduct(record, 1)}
              />
            )}
          </Space>
        );
      },
    },
    {
      title: "ชื่อสินค้า",
      dataIndex: "name",
      key: "name",
      width: 600,
      render: (text, record) => {
        const groupInventory = inventoryByGroupId.get(record?.key) || [];
        const productApp = appList?.find(
          (app) => app?.nameLower === record?.app,
        );

        return (
          <div>
            <span style={{ color: "white", fontWeight: 600 }}>{text}</span>
            <div className="neo-desktop-product-inventory">
              {loadingInv || inventoryError ? (
                <InventoryLoadingState
                  loading={loadingInv}
                  error={inventoryError}
                  onRetry={loadInventory}
                />
              ) : groupInventory?.length ? (
                groupInventory.map((inventoryItem) => {
                  const isPermanent = productApp?.type === "permanent";
                  const inventoryState = isPermanent
                    ? "success"
                    : getInventoryState(inventoryItem);

                  return (
                  <div
                    key={inventoryItem.optionId}
                    className={`neo-inventory-card ${
                      inventoryState === "success" ? "" : inventoryState
                    }`}
                  >
                    <AdminImage
                      className="neo-inventory-logo"
                      src={productApp?.logo?.url || "/noImage.jpg"}
                      retryVersion={imageRetryVersion}
                      alt="logo"
                    />
                    <div className="neo-inventory-info">
                      <div className="neo-inventory-app">
                        {inventoryItem.optionName}
                      </div>
                      <div className="neo-inventory-stock">
                        <span
                          className={`neo-stock-dot ${
                            inventoryState
                          }`}
                        />
                        {isPermanent ? (
                          "ถาวร"
                        ) : inventoryItem.stock <= 0 ? (
                          <span className="neo-stock-out">สินค้าหมด</span>
                        ) : (
                          `${inventoryItem.stock.toLocaleString()} จอ`
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })
              ) : (
                <span className="neo-desktop-product-inventory-empty">
                  ยังไม่มีสินค้า
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "App",
      dataIndex: "app",
      key: "app",
      width: 200,
      align: "center",
      render: (text) =>
        text ? (
          <AdminImage
            src={appList?.find((ap) => ap?.nameLower === text)?.logo?.url || "/noImage.jpg"}
            retryVersion={imageRetryVersion}
            alt="logoImage"
            style={{ width: "42px", height: "42px", borderRadius: "12px" }}
          />
        ) : null,
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 200,
      render: (text, record) => (
        <span
          style={{
            color: record?.cloneTo
              ? "#f59e0b"
              : text === "available"
                ? "#22c55e"
                : "#ef4444",
            fontWeight: 600,
          }}
        >
          {record?.cloneTo ? "Cloned" : text}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            onClick={() => {
              navigate("/admin/product/" + record?.key, { replace: true });
            }}
            className="neo-btn-show"
          >
            <EyeOutlined />
            {/* ดูรายละเอียด */}
          </Button>
          {record?.status === "deleted" ? null : (
            <CreateProductAdminPage
              groupId={record.key}
              inventory={inventoryByGroupId.get(record?.key) || []}
              appList={appList?.find((al) => al?.nameLower === record?.app)}
              refreshInventory={loadInventory}
            />
          )}
          {record?.cloneTo || record?.status === "deleted" ? null : (
            <UpdateProductGroupModal groupData={record} fetchData={fetchData} />
          )}
          {record?.status === "deleted" ? null : (
            <Button
              className="neo-btn-delete"
              onClick={() => handleDelete(record)}
            >
              <DeleteOutlined />
            </Button>
          )}
          {record?.cloneTo || record?.status === "deleted" ? null : (
            <CloneModal
              fetchData={fetchData}
              refreshInventory={loadInventory}
              groupData={record}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <AdminLayout title="สินค้า">
      <div className="neo-search-page neo-admin-page">
        <div className="neo-admin-wrap">
          <div className="neo-admin-table-card">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <div className="neo-table-title" style={{ paddingTop: "14px" }}>
                PRODUCT MANAGEMENT
              </div>
              {!isMobile ? (
                <div
                  style={{
                    columnGap: "8px",
                    rowGap: "8px",
                    display: "flex",
                    marginBottom: "16px",
                    flexWrap: "wrap",
                  }}
                >
                  <CreateModal appList={appList} fetchData={fetchData} />
                  <CreateOrderModal fetchData={fetchData} />
                </div>
              ) : null}
            </div>

            {isMobile ? (
              <div
                style={{
                  columnGap: "8px",
                  rowGap: "8px",
                  display: "flex",
                  marginBottom: "16px",
                  flexWrap: "wrap",
                }}
              >
                <CreateModal appList={appList} fetchData={fetchData} />
                <CreateOrderModal fetchData={fetchData} />
              </div>
            ) : null}

            <div className="neo-table-toolbar" style={{ marginBottom: "20px" }}>
              <Input
                allowClear
                placeholder="ค้นหาชื่อสินค้า หรือ App"
                onChange={(e) => {
                  setKeyword(e.target.value);
                }}
                className="neo-search-input"
              />
            </div>

            {dataLoadError ? (
              <div style={{ color: "#f87171", marginBottom: 16 }}>
                {dataLoadError} <Button size="small" onClick={fetchData}>ลองใหม่</Button>
              </div>
            ) : null}

            {isMobile ? (
              <>
                <div className="neo-mobile-list">
                  {pagedData?.map((item) => {
                    const appPeoduct = appList?.find(
                      (al) => al?.nameLower === item?.app,
                    );
                    const groupInventory = inventoryByGroupId.get(item.key) || [];
                    return (
                      <div
                        key={item.key}
                        className="neo-app-mobile-card"
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          navigate("/admin/product/" + item.key, {
                            replace: true,
                          })
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            navigate("/admin/product/" + item.key, {
                              replace: true,
                            });
                          }
                        }}
                      >
                        <div className="neo-app-card-content">
                          {/* LOGO LEFT */}

                          <Space direction="vertical" size={2} className="neo-mobile-sort-buttons">
                            {data.findIndex((product) => product.key === item.key) > 0 && (
                              <Button
                                className="neo-mobile-sort-button"
                                type="text"
                                icon={<ArrowUpOutlined />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  moveProduct(item, -1);
                                }}
                              />
                            )}
                            {data.findIndex((product) => product.key === item.key) <
                              data.length - 1 && (
                              <Button
                                className="neo-mobile-sort-button"
                                type="text"
                                icon={<ArrowDownOutlined />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  moveProduct(item, 1);
                                }}
                              />
                            )}
                          </Space>

                          <div className="neo-app-logo-box">
                            <AdminImage
                              src={appPeoduct?.logo?.url}
                              retryVersion={imageRetryVersion}
                              alt="logo"
                              className="neo-app-mobile-logo"
                            />
                          </div>

                          {/* DATA RIGHT */}

                          <div className="neo-app-mobile-info">
                            {/* STATUS */}

                            <div
                              className={`neo-app-status ${
                                item.status === "inactive"
                                  ? "inactive"
                                  : "active"
                              }`}
                            >
                              <span />

                              {item.status}
                            </div>

                            {/* NAME */}

                            <div className="neo-app-mobile-name">
                              {item.name}
                            </div>
                          </div>

                          {/* DRAWER BUTTON */}

                          <Button
                            className="neo-app-more"
                            type="text"
                            icon={<MoreOutlined />}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectProduct(item);
                              setOpenDrawer(true);
                            }}
                          />
                        </div>

                        <div className="neo-mobile-product-inventory">
                          <div className="neo-mobile-product-inventory-title">
                            ยอดสินค้าในระบบ
                          </div>
                          <div className="neo-mobile-product-inventory-list">
                            {loadingInv || inventoryError ? (
                              <InventoryLoadingState
                                loading={loadingInv}
                                error={inventoryError}
                                onRetry={loadInventory}
                              />
                            ) : groupInventory?.length ? (
                              groupInventory.map((inventoryItem) => {
                                const isPermanent = appPeoduct?.type === "permanent";
                                const inventoryState = isPermanent
                                  ? "success"
                                  : getInventoryState(inventoryItem);

                                return (
                                <div
                                  key={inventoryItem.optionId}
                                  className={`neo-inventory-card ${
                                    inventoryState === "success" ? "" : inventoryState
                                  }`}
                                >
                                  <AdminImage
                                    className="neo-inventory-logo"
                                    src={appPeoduct?.logo?.url || "/noImage.jpg"}
                                    retryVersion={imageRetryVersion}
                                    alt="logo"
                                  />
                                  <div className="neo-inventory-info">
                                    <div className="neo-inventory-app">
                                      {inventoryItem.optionName}
                                    </div>
                                    <div className="neo-inventory-stock">
                                  <span
                                    className={`neo-stock-dot ${
                                      inventoryState
                                    }`}
                                  />
                                      {isPermanent ? (
                                        "ถาวร"
                                      ) : inventoryItem.stock <= 0 ? (
                                        <span className="neo-stock-out">
                                          สินค้าหมด
                                        </span>
                                      ) : (
                                        `${inventoryItem.stock.toLocaleString()} จอ`
                                      )}
                                    </div>
                                  </div>
                                </div>
                                );
                              })
                            ) : (
                              <span className="neo-mobile-product-stock-empty">
                                ยังไม่มีสินค้า
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Drawer
                  rootClassName="neo-floating-drawer"
                  placement="bottom"
                  height={320}
                  open={openDrawer}
                  onClose={() => {
                    setOpenDrawer(false);
                  }}
                  closable={false}
                >
                  <Col span={24} style={{ marginBottom: 8 }}>
                    <Space style={{ width: "100%" }}>
                      <Button
                        className="neo-btn-show"
                        icon={<ArrowUpOutlined />}
                        disabled={
                          data.findIndex((item) => item.key === selectProduct?.key) === 0
                        }
                        onClick={() => moveProduct(selectProduct, -1)}
                        style={{ flex: 1 }}
                      >
                        ขึ้น
                      </Button>
                      <Button
                        className="neo-btn-show"
                        icon={<ArrowDownOutlined />}
                        disabled={
                          data.findIndex((item) => item.key === selectProduct?.key) ===
                          data.length - 1
                        }
                        onClick={() => moveProduct(selectProduct, 1)}
                        style={{ flex: 1 }}
                      >
                        ลง
                      </Button>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Button
                      className="neo-btn-show"
                      onClick={() => {
                        navigate("/admin/product/" + selectProduct?.key, {
                          replace: true,
                        });
                      }}
                      style={isMobile ? { width: "100%" } : {}}
                      icon={isMobile ? <EyeOutlined /> : ""}
                    >
                      ดูรายละเอียด
                    </Button>
                  </Col>
                  <Col span={24} style={{ marginTop: 8 }}>
                    {selectProduct?.cloneTo ? null : (
                      <UpdateProductGroupModal
                        groupData={selectProduct}
                        fetchData={fetchData}
                        isMobile={isMobile}
                        onOpen={() => setOpenDrawer(false)}
                      />
                    )}
                  </Col>
                  <Col span={24} style={{ marginTop: 8 }}>
                    {selectProduct?.status === "deleted" ? null : (
                      <Button
                        className="neo-btn-delete"
                        onClick={() => handleDelete(selectProduct)}
                        style={isMobile ? { width: "100%" } : {}}
                        icon={isMobile ? <DeleteOutlined /> : ""}
                      >
                        ลบ
                      </Button>
                    )}
                  </Col>
                  <Col span={24} style={{ marginTop: 8 }}>
                    {selectProduct?.cloneTo ||
                    selectProduct?.status === "deleted" ? null : (
                      <CloneModal
                        fetchData={fetchData}
                        refreshInventory={loadInventory}
                        groupData={selectProduct}
                        isMobile={isMobile}
                        onOpen={() => setOpenDrawer(false)}
                      />
                    )}
                  </Col>
                </Drawer>
              </>
            ) : (
              <Table
                dataSource={filteredData}
                columns={columns}
                rowKey="key"
                pagination={false}
                className="neo-table"
              />
            )}
          </div>
        </div>

        {/* STYLE */}
        <style>{`
        :root {
          --cinema-red: #e5312a;
          --cinema-red-rgb: 229, 49, 42;
          --cinema-red-dark: #b31d18;
          --cinema-red-dark-rgb: 179, 29, 24;
          --cinema-gold: #f8cc46;
          --cinema-gold-rgb: 248, 204, 70;
          --cinema-gold-light: #ffeb3b;
          --cinema-gold-light-rgb: 255, 235, 59;
          --cinema-white: #ffffff;
          --cinema-white-rgb: 255, 255, 255;
          --cinema-black: #1a1a1a;
          --cinema-black-rgb: 26, 26, 26;
        }
        

        .neo-admin-title{
          font-weight:900;
          letter-spacing:2px;
        }

        .neo-admin-user{
          display:flex;
          gap:12px;
          align-items:center;
          font-weight:600;
        }

        .neo-admin-wrap{
          position:relative;
          z-index:2;
          padding:120px 40px 40px;
          display:flex;
          justify-content:center;
        }

        .neo-admin-page{
          height:auto !important;
          min-height:100vh;
          min-height:100dvh;
          overflow:visible !important;
          overscroll-behavior-y:auto;
        }

        .neo-admin-table-card{
          width:100%;
          max-width:1500px;

          padding:24px;
          border-radius:28px;

          background:rgba(var(--cinema-black-rgb), 0.6); /* Darker, themed background */
          backdrop-filter:blur(26px);
          border:1px solid rgba(var(--cinema-white-rgb), 0.08);
        }

        .neo-table-title{
          color:var(--cinema-white);
          font-size:18px;
          font-weight:900;
          letter-spacing:2px;
          margin-bottom:16px;
        }
        
        /* TOOLBAR & SEARCH */
        .neo-search-input {
          width: 100%;
          max-width: 400px;
          height: 48px !important;
          border: none !important;
          background: rgba(255, 255, 255, 0.9) !important;
          color: var(--cinema-black) !important; /* Text color for input */
          border-radius: 14px !important;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        /* TABLE */
        .neo-table .ant-table{
          background:transparent !important;
          color:var(--cinema-white);
        }

        .neo-table .ant-table-thead > tr > th{
          background:rgba(var(--cinema-red-rgb), 0.1) !important; /* Themed header background */
          color:var(--cinema-gold) !important; /* Themed header text */
          border-bottom:1px solid rgba(var(--cinema-white-rgb), 0.08) !important;
        }

        .neo-table .ant-table-tbody > tr > td{
          background:transparent !important;
          color:var(--cinema-white) !important; /* Table body text color */
          border-bottom:1px solid rgba(var(--cinema-white-rgb), 0.06) !important;
        }
        
        .neo-table .ant-table-tbody > tr:hover > td{
          background:rgba(255,255,255,0.05) !important;
        }

        /* PAGINATION */
        .ant-pagination {
          display: flex;
          justify-content: center;
          gap: 6px;
        }

        .ant-pagination-item,
        .ant-pagination-prev,
        .ant-pagination-next,
        .ant-pagination-jump-prev,
        .ant-pagination-jump-next{
          background: rgba(var(--cinema-white-rgb), 0.08) !important;
          border: 1px solid rgba(255, 255, 255, 0.05) !important;
          border-radius: 12px !important;
          min-width: 38px !important;
          height: 38px !important;
          line-height: 36px !important;
          transition: all 0.3s ease;
        }

        .ant-pagination-item a{
          color: rgba(var(--cinema-white-rgb), 0.85) !important;
          font-weight: 600 !important;
        }

        .ant-pagination-item-active{
          background: linear-gradient(135deg, var(--cinema-red), var(--cinema-gold)) !important; /* Themed active pagination */
          border: none !important;
        }

        .ant-pagination-item-active a{
          color: var(--cinema-white) !important;
        }

        .ant-pagination-item:hover, .ant-pagination-prev:hover, .ant-pagination-next:hover{
          background: rgba(var(--cinema-white-rgb), 0.15) !important;
          transform: translateY(-2px);
        }

        .ant-pagination-prev .ant-pagination-item-link,
        .ant-pagination-next .ant-pagination-item-link{
          color: var(--cinema-white) !important;
          background: transparent !important;
        }

        .ant-pagination-disabled .ant-pagination-item-link{
          color: rgba(var(--cinema-white-rgb), 0.2) !important;
        }

        .ant-pagination-jump-prev .ant-pagination-item-container .ant-pagination-item-ellipsis,
        .ant-pagination-jump-next .ant-pagination-item-container .ant-pagination-item-ellipsis{
          color: rgba(var(--cinema-white-rgb), 0.4) !important;
        }
        
        /* BUTTONS */
        .neo-btn-show{
          background:rgba(var(--cinema-gold-rgb), 0.15) !important; /* Themed show button */
          border:1px solid rgba(var(--cinema-gold-rgb), 0.3) !important;
          color:var(--cinema-gold-light) !important;
          font-weight:700;
          border-radius:10px;
          padding: 0 10px ;
        }

        .neo-btn-show:hover{
          transform:translateY(-2px);
        }

        .neo-btn-delete{
          background:rgba(var(--cinema-red-rgb), 0.15) !important; /* Themed delete button */
          border:1px solid rgba(var(--cinema-red-rgb), 0.3) !important;
          color:var(--cinema-red) !important;
          font-weight:700;
          border-radius:10px;
          padding: 0 10px ;
        }

        .neo-btn-delete:hover{
          transform:translateY(-2px);
        }

        /* PRIMARY BUTTON (e.g., เพิ่มสินค้า) */
        .ant-btn-primary, .neo-btn-primary {
          background: linear-gradient(135deg, var(--cinema-red), var(--cinema-gold)) !important;
          border: none !important;
          color: white !important;
          font-weight: 800 !important;
          border-radius: 12px !important;
          height: 42px !important;
          box-shadow: 0 4px 15px rgba(var(--cinema-red-rgb), 0.35) !important;
          transition: all 0.3s ease !important;
        }

        .ant-btn-primary:hover, .neo-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(var(--cinema-red-rgb), 0.45) !important;
          filter: brightness(1.1);
        }

        /* MOBILE */
        .neo-mobile-float{
          display:none;
        }

        @media (max-width: 1180px), (min-width: 1181px) and (max-width: 1366px) and (pointer: coarse){
          .neo-admin-wrap{
            padding:100px 16px 40px;
          }


          .neo-mobile-float{
            display:flex;
            justify-content:space-between;
            align-items:center;

            position:fixed;
            bottom:20px;
            left:20px;
            right:20px;

            padding:14px 18px;
            border-radius:18px;

            background:rgba(var(--cinema-black-rgb), 0.7); /* Themed mobile float background */
            backdrop-filter:blur(20px);
            border:1px solid rgba(var(--cinema-white-rgb), 0.1);
            color:var(--cinema-white);
            z-index:999;
          }

          .ant-table-content{
            overflow-x:auto;
          }
            // .neo-delete-btn {
            // width: 100%;
            // margin-inline-start: 0px !important;
            // }
        }
          .neo-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}



.neo-status {
  padding: 6px 12px;
  border-radius: 999px;

  font-size: 11px;
  font-weight: 700;
}

.neo-status-waiting {
  background: rgba(245,158,11,.15);
  color: #fbbf24;
}

.neo-status-sent {
  background: rgba(59,130,246,.15);
  color: #60a5fa;
}

.neo-status-claimed {
  background: rgba(16,185,129,.15);
  color: #34d399;
}

.neo-mobile-pagination {
  margin-top: 24px;
  margin-bottom: 40px;
  text-align: center;
}

/* confirm text */

.neo-confirm-title{
  color:white !important;

  font-size:22px;
  font-weight:900;
}

.neo-confirm-message{
  color:white;
}

.neo-delete-box{
  margin-top:12px;

  padding:12px 14px;

  border-radius:14px;

  background:
    rgba(239,68,68,.12);

  border:
    1px solid rgba(239,68,68,.25);

  color:#fca5a5;

  font-weight:700;
}

/* cancel */

.neo-cancel-btn{
  height:44px !important;

  border:none !important;

  border-radius:14px !important;

  background:
    rgba(255,255,255,.08) !important;

  color:white !important;

  font-weight:700 !important;
}

.neo-cancel-btn:hover{
  background:
    rgba(255,255,255,.14) !important;

  color:white !important;
}

/* delete */

.neo-delete-btn{
  height:44px !important;

  border:none !important;

  border-radius:14px !important;

  background:
    linear-gradient(
      135deg,
      #dc2626,
      #991b1b
    ) !important;

  color:white !important;

  font-weight:800 !important;

  box-shadow:
    0 10px 25px rgba(220,38,38,.35);
}

.neo-delete-btn:hover{
  filter:brightness(1.05);
}
.neo-inventory-wrap{
  margin:12px 0px;
}

.neo-desktop-product-inventory{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:10px;
}

.neo-desktop-product-inventory .neo-inventory-card{
  min-width:150px;
  padding:8px 10px;
  gap:8px;
  border-radius:12px;
}

.neo-desktop-product-inventory .neo-inventory-logo{
  width:30px;
  height:30px;
  border-radius:9px;
}

.neo-desktop-product-inventory .neo-inventory-app{
  font-size:11px;
}

.neo-desktop-product-inventory .neo-inventory-stock{
  font-size:12px;
}

.neo-desktop-product-inventory-empty{
  color:rgba(255,255,255,.45);
  font-size:12px;
}

.neo-inventory-title{
  color:rgba(var(--cinema-white-rgb), .7);

  font-size:12px;
  font-weight:800;

  letter-spacing:2px;

  margin-bottom:12px;
}

.neo-inventory-list{
  display:flex;
  flex-wrap:wrap;

  gap:12px;
}

.neo-inventory-card{
  display:flex;
  align-items:center;

  gap:12px;

  padding:8px 12px;

  border-radius:18px;

  background:
    rgba(var(--cinema-white-rgb), .05);

  border:
    1px solid rgba(var(--cinema-white-rgb), .08);

  backdrop-filter:blur(16px);

  transition:.25s;
}

.neo-inventory-card.warning {
background:
    rgba(245,158,11,.80);

  border:
    1px solid rgba(245,158,11,.80);
}

.neo-inventory-card.danger {
background:
    rgba(239,68,68,.80);

  border:
    1px solid rgba(239,68,68,.80);
}

.neo-inventory-card:hover{
  transform:translateY(-2px);

  border-color:
    rgba(var(--cinema-gold-rgb), .25);
}

.neo-inventory-logo{
  width:42px;
  height:42px;

  border-radius:12px;

  object-fit:cover;
}

.neo-inventory-info{
  display:flex;
  flex-direction:column;
}

.neo-inventory-app{
  color:white;

  font-size:13px;
  font-weight:700;
}

.neo-inventory-stock{
   display:flex;
  align-items:center;
  gap:8px;

  margin-top:6px;

  font-size:13px;
  font-weight:700;

  color:#fff;
}

@media (max-width: 1180px), (min-width: 1181px) and (max-width: 1366px) and (pointer: coarse){

  .neo-inventory-list{
    gap:6px;
  }

  .neo-inventory-card{
    // flex:1;

    min-width:120px;

    padding:8px 10px;
  }

  .neo-inventory-logo{
    width:35px;
    height:35px;
  }

  .neo-inventory-stock{
  font-size:13px;
}

  .neo-inventory-app{
    font-size:9px;
  }
}

.neo-stock-dot{
  width:10px;
  height:10px;
  border-radius:50%;
  flex-shrink:0;

  animation:pulseStock 2s infinite;
}

.neo-stock-dot.success{
  background:#22c55e;
  box-shadow:0 0 10px rgba(34,197,94,.6);
}

.neo-stock-dot.warning{
  background:#f59e0b;
  box-shadow:0 0 10px rgba(245,158,11,.6);
}

.neo-stock-dot.danger{
  background:#ef4444;
  box-shadow:0 0 10px rgba(239,68,68,.7);
}

.neo-stock-out{
  color:#ffffff;
  font-weight:800;
}

@keyframes pulseStock{
  0%{
    transform:scale(1);
    opacity:1;
  }
  50%{
    transform:scale(1.25);
    opacity:.6;
  }
  100%{
    transform:scale(1);
    opacity:1;
  }
}


.neo-app-mobile-card{

 padding:16px;

 border-radius:22px;

 background:
 rgba(26,26,26,.7);

 border:
 1px solid rgba(255,255,255,.08);

 backdrop-filter:blur(20px);

 cursor:pointer;

 transition:transform .2s ease, border-color .2s ease;

}

.neo-app-mobile-card:active{
 transform:scale(.98);
 border-color:rgba(248,204,70,.65);
}


.neo-app-card-content{

 display:flex;

 align-items:center;

 position:relative;

}


/* LOGO */

.neo-app-logo-box{

 flex-shrink:0;

 margin-right:16px;

}


.neo-app-mobile-logo{

 width:72px;

 height:72px;

 border-radius:20px;

 object-fit:cover;

}


/* INFO */

.neo-app-mobile-info{

 display:flex;

 flex-direction:column;

 gap:6px;

 flex:1;

 min-width:0;

 padding-right:30px;

}


/* STATUS */

.neo-app-status{

 display:flex;

 align-items:center;

 gap:7px;

 font-size:12px;

 font-weight:800;

 text-transform:capitalize;

}


.neo-app-status span{

 width:9px;

 height:9px;

 border-radius:50%;

}


.neo-app-status.active{

 color:#22c55e;

}


.neo-app-status.active span{

 background:#22c55e;

 box-shadow:
 0 0 10px #22c55e;

}


.neo-app-status.inactive{

 color:#ef4444;

}


.neo-app-status.inactive span{

 background:#ef4444;

 box-shadow:
 0 0 10px #ef4444;

}



/* NAME */

.neo-app-mobile-name{

 color:white;

 font-size:17px;

 font-weight:900;

 line-height:1.35;

 min-height:calc(17px * 1.35 * 2);

 overflow:hidden;

 display:-webkit-box;

 -webkit-box-orient:vertical;

 -webkit-line-clamp:2;

}

.neo-mobile-product-inventory{
 margin-top:14px;
 padding-top:12px;
 border-top:1px solid rgba(255,255,255,.08);
}

.neo-mobile-product-inventory-title{
 color:rgba(255,255,255,.62);
 font-size:11px;
 font-weight:800;
 letter-spacing:.4px;
 margin-bottom:8px;
 text-align:left;
}

.neo-mobile-product-inventory-list{
 display:grid;
 grid-template-columns:repeat(2, minmax(0, 1fr));
 gap:7px;
}

.neo-mobile-product-inventory-list .neo-inventory-card{
 min-width:0;
 width:100%;
 min-height:52px;
 padding:7px 8px;
 gap:7px;
 border-radius:12px;
}

.neo-mobile-product-inventory-list .neo-inventory-logo{
 width:28px;
 height:28px;
 border-radius:8px;
}

.neo-mobile-product-inventory-list .neo-inventory-info{
 min-width:0;
 gap:2px;
}

.neo-mobile-product-inventory-list .neo-inventory-app{
 overflow:hidden;
 text-overflow:ellipsis;
 white-space:nowrap;
 font-size:10px;
 line-height:1.2;
}

.neo-mobile-product-inventory-list .neo-inventory-stock{
 gap:5px;
 font-size:11px;
 line-height:1.2;
 white-space:nowrap;
}

.neo-mobile-product-stock{
 display:flex;
 align-items:center;
 gap:6px;
 max-width:100%;
 padding:6px 9px;
 border:1px solid rgba(34,197,94,.28);
 border-radius:10px;
 background:rgba(34,197,94,.1);
 color:#dcfce7;
 font-size:12px;
 text-align:left;
}

.neo-mobile-product-stock.warning{
 border-color:rgba(245,158,11,.35);
 background:rgba(245,158,11,.12);
 color:#fef3c7;
}

.neo-mobile-product-stock.danger{
 border-color:rgba(239,68,68,.4);
 background:rgba(239,68,68,.13);
 color:#fee2e2;
}

.neo-mobile-product-stock strong{
 color:inherit;
 white-space:nowrap;
}

.neo-mobile-product-stock .neo-stock-dot{
 width:7px;
 height:7px;
}

.neo-mobile-product-stock-empty{
 color:rgba(255,255,255,.45);
 font-size:12px;
}



/* MORE BUTTON */

.neo-app-more{

 position:absolute;

 right:0;

 top:0;

 color:white !important;

 font-size:22px;

 width:44px;

 height:44px;

 padding:0 !important;

 display:flex;

 align-items:center;

 justify-content:center;

 z-index:2;

}


.neo-app-more:hover{

 background:
 rgba(255,255,255,.1)!important;

}

.neo-mobile-sort-buttons{
 display:flex;
 flex-direction:column;
 flex-shrink:0;
 margin-right:10px;
}

.neo-mobile-sort-button{
 width:28px;
 height:28px;
 padding:0 !important;
 color:#f8cc46 !important;
 border:1px solid rgba(248,204,70,.3) !important;
 border-radius:8px !important;
 background:rgba(248,204,70,.1) !important;
}

.neo-mobile-sort-button:hover{
 background:rgba(248,204,70,.2) !important;
}

/* Compact mobile card layout */
.neo-app-logo-box{
 margin-right:12px;
}

.neo-app-mobile-logo{
 width:56px;
 height:56px;
 border-radius:16px;
}

.neo-app-status{
 font-size:10px;
}

.neo-app-mobile-name{
 font-size:14px;
 min-height:calc(14px * 1.35 * 2);
}

/* ===================================
 Floating Bottom Drawer
=================================== */


.neo-floating-drawer
.ant-drawer-content-wrapper{


 bottom:20px !important;


 height:auto !important;


 border-radius:28px !important;


 overflow:hidden;


 margin:0 16px;


}


.neo-floating-drawer
.ant-drawer-content{


 border-radius:28px !important;


 background:
 rgba(26,26,26,.85) !important;


 backdrop-filter:
 blur(25px);


 border:
 1px solid rgba(255,255,255,.1);


 box-shadow:

 0 20px 60px rgba(0,0,0,.5);

}



.neo-floating-drawer
.ant-drawer-header{


 display:none;


}



.neo-floating-drawer
.ant-drawer-body{


 padding:22px !important;


}



/* Header */


.neo-drawer-header{


 display:flex;

 justify-content:space-between;

 align-items:center;

 margin-bottom:20px;


}



.neo-drawer-title{


 color:white;

 font-size:20px;

 font-weight:900;


}



.neo-drawer-sub{


 color:
 rgba(255,255,255,.5);

 font-size:13px;


}




/* Action */


.neo-drawer-actions{


 display:flex;

 flex-direction:column;

 gap:12px;


}



.neo-drawer-actions button{


 height:48px;

 border-radius:16px;


 background:
 rgba(255,255,255,.08);


 border:
 1px solid rgba(255,255,255,.08);


 color:white;


 font-weight:700;


}



.neo-drawer-actions button:hover{


 background:
 rgba(248,204,70,.15);


 color:
 #f8cc46;


}
      `}</style>
      </div>
    </AdminLayout>
  );
}
